/**
 * Relative-on-disk, app-absolute-at-render.
 *
 * The markdown a page stores keeps a **relative** link (`./assets/shot.png`),
 * so the file still renders on GitHub or in a plain checkout. Only the render
 * rewrites it to the `/api/blob/…` route that streams the repo file — which
 * also means the draft worktree's copy is what an editor sees before publish.
 *
 * Both the rendered page and the rich editor go through here, so neither can
 * drift into writing an app-absolute URL back into the file.
 */

/** Directory portion of a repo path ("a/b/c.md" → "a/b", "c.md" → ""). */
export function dirOf(p: string): string {
  const i = p.lastIndexOf("/");
  return i === -1 ? "" : p.slice(0, i);
}

/** Resolve a relative repo path against a base directory (handles ./ and ../). */
export function joinRepo(baseDir: string, rel: string): string {
  const parts = baseDir ? baseDir.split("/") : [];
  for (const seg of rel.split("/")) {
    if (!seg || seg === ".") continue;
    if (seg === "..") parts.pop();
    else parts.push(seg);
  }
  return parts.join("/");
}

/**
 * Map a markdown asset URL to something the browser can fetch. Absolute and
 * external URLs pass through; relative repo links become /api/blob/<path>.
 */
export function assetSrc(url: string, baseDir: string, repo: string): string {
  if (
    !url ||
    url.startsWith("/") ||
    url.startsWith("#") ||
    /^[a-z][a-z0-9+.-]*:/i.test(url) // http:, https:, data:, blob:, mailto:, …
  ) {
    return url;
  }
  const repoPath = joinRepo(baseDir, url);
  if (!repoPath) return url;
  const prefix = repo ? `/api/r/${encodeURIComponent(repo)}` : "/api";
  return `${prefix}/blob/${repoPath.split("/").map(encodeURIComponent).join("/")}`;
}
